import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    SvgIcon,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as ExecutivesIcon } from '../../static/sustainability_image/executives_icon.svg';
import { ReactComponent as BoardMembersIcon } from '../../static/sustainability_image/boardMembers_icon.svg';
import { useResponsive } from '../../contexts/ResponsiveContext';

import { AboutSustainabilitySocialContent } from '../../graphql/pimcore/modules/siteContent/types/AboutSustainabilityContent';
import type { CompanyFocusTopicElement } from '../../graphql/pimcore/modules/siteContent/types/SiteContentElement';
import { sanitizeRichText } from '../../graphql/pimcore/modules/siteContent/tools/sanitizeRichText';
import { useTabUrl } from '../common/useTabUrl';

interface SocialProps {
    cmsContent: AboutSustainabilitySocialContent;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    style?: React.CSSProperties;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, style, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{ cursor: 'default', ...style }}
            {...other}
        >
            {value === index && (
                <Box sx={{ marginTop: '0.42rem', width: '100%', cursor: 'default' }}>
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

interface CustomIconProps {
    component: React.ElementType;
    expanded: boolean;
}
interface InternalLinkItem {
    kind: 'internalLinkFieldCollection';
    linkText: string;
    linkRoute: string | null;
}
interface ExternalLinkItem {
    kind: 'externalLinkFieldCollection';
    linkText: string;
    linkUrl: string | null;
}
const CustomIcon: FunctionComponent<CustomIconProps> = ({ component: Component, expanded }) => (
    <SvgIcon
        component={Component}
        inheritViewBox
        sx={{
            marginRight: '0.5rem',
            width: '24px',
            height: '24px',
            path: { fill: expanded ? 'white' : '#E56731' },
        }}
    />
);

const SocialTab: FunctionComponent<SocialProps> = ({ cmsContent }) => {
    const [mobileExpanded, setMobileExpanded] = useState<number | false>(false);
    const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
    const basePath = '/About/Sustainability/Social';
    const tabKeys = ['YageoFocus', 'KemetFocus'];
    const { value, handleChange } = useTabUrl(basePath, tabKeys);

    type LinkItem = InternalLinkItem | ExternalLinkItem;

    const resolveHref = (item: LinkItem) =>
        item.kind === 'internalLinkFieldCollection'
            ? item.linkRoute || '#'
            : item.kind === 'externalLinkFieldCollection'
              ? item.linkUrl || '#'
              : '#';

    const handleMobileAccordionChange =
        (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setMobileExpanded(isExpanded ? panel : false);
        };

    const { isScreenXxl, isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } =
        useResponsive();
    const isDesktop = isScreenLg || isScreenXl || isScreenXxl;

    const FocusAccordion = ({ entries }: { entries: CompanyFocusTopicElement[] }) => (
        <Accordion
            expanded={true}
            square
            sx={{
                backgroundColor: '#FAE1D8',
                width: isScreenLg ? '565px' : isScreenXl ? '760px' : !isDesktop ? '100%' : '769px',
            }}
        >
            <AccordionSummary>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {entries.map((topic, topicIdx) => (
                        <Box key={topicIdx} sx={{ mb: 2 }}>
                            <Typography
                                sx={{
                                    fontSize: isScreenSm || isScreenXs ? '16px' : '30px',
                                    color: '#B75332',
                                    fontWeight: 700,
                                    fontFamily: 'Poppins',
                                    mb: 2,
                                }}
                            >
                                {topic.title}
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                }}
                            >
                                {topic.entries.map((item, entryIdx) => {
                                    const isContent = item.kind === 'contentSectionFieldCollection';
                                    const panelKey = `${topicIdx}-${entryIdx}`;

                                    return (
                                        <Accordion
                                            key={panelKey}
                                            disableGutters
                                            expanded={
                                                isContent ? !!expandedMap[panelKey] : undefined
                                            }
                                            onChange={
                                                isContent
                                                    ? (_, isExpanded) =>
                                                          setExpandedMap((prev) => ({
                                                              ...prev,
                                                              [panelKey]: isExpanded,
                                                          }))
                                                    : undefined
                                            }
                                            sx={{ width: '100%', mb: 2.5 }}
                                        >
                                            <AccordionSummary
                                                expandIcon={
                                                    isContent ? (
                                                        expandedMap[panelKey] ? (
                                                            <RemoveIcon />
                                                        ) : (
                                                            <AddIcon />
                                                        )
                                                    ) : (
                                                        <LinkIcon
                                                            sx={{ fontSize: 35, color: 'black' }}
                                                        />
                                                    )
                                                }
                                                {...(!isContent
                                                    ? {
                                                          component: 'a',
                                                          href: resolveHref(item),
                                                          target: '_blank',
                                                          rel: 'noopener noreferrer',
                                                      }
                                                    : {})}
                                                sx={{
                                                    backgroundColor:
                                                        isContent && expandedMap[panelKey]
                                                            ? '#F5C3B2'
                                                            : 'white',
                                                    pl: 2,
                                                    pr: 2,
                                                    width: '100%',
                                                    cursor: 'pointer',
                                                    transition: 'background-color 0.3s',
                                                    '&:hover': { backgroundColor: '#F5C3B2' },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize:
                                                                isScreenSm || isScreenXs
                                                                    ? '16px'
                                                                    : '20px',
                                                            color: '#3E3E3E',
                                                            fontWeight: 500,
                                                            fontFamily: 'Poppins',
                                                        }}
                                                    >
                                                        {isContent ? item.title : item.linkText}
                                                    </Typography>
                                                </Box>
                                            </AccordionSummary>

                                            {isContent && (
                                                <AccordionDetails
                                                    sx={{
                                                        cursor: 'default',
                                                        p: isScreenSm || isScreenXs ? 2 : 4,
                                                        fontFamily: 'Poppins',
                                                        fontSize: isDesktop ? '16px' : '12px',
                                                        fontWeight:
                                                            isScreenSm || isScreenXs ? 400 : 500,
                                                        '& a': {
                                                            color: '#B75332',
                                                            fontWeight: 500,
                                                        },
                                                        '& a:hover': {
                                                            color: '#E5673E',
                                                        },
                                                    }}
                                                >
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: sanitizeRichText(
                                                                item.content,
                                                                ['ul', 'li', 'a'],
                                                                ['href', 'target']
                                                            ),
                                                        }}
                                                    />
                                                </AccordionDetails>
                                            )}
                                        </Accordion>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </AccordionSummary>
        </Accordion>
    );

    const mobileTabs = [
        {
            label: cmsContent.YageoFocus.title.content,
            icon: ExecutivesIcon,
            content: <FocusAccordion entries={cmsContent.YageoFocus.entries} />,
        },
        {
            label: cmsContent.KemetFocus.title.content,
            icon: BoardMembersIcon,
            content: <FocusAccordion entries={cmsContent.KemetFocus.entries} />,
        },
    ];

    return (
        <>
            {isDesktop ? (
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: '#F8F8F8',
                        display: 'flex',
                        height: '100%',
                    }}
                >
                    <Tabs
                        orientation={isScreenMd ? 'horizontal' : 'vertical'}
                        value={value}
                        onChange={handleChange}
                        aria-label="Vertical tabs example"
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{
                            width: isScreenLg ? '20rem' : '22rem',
                            position: 'relative',
                            cursor: 'default',
                            '& .MuiTab-root': {
                                color: '#E5673E',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                padding: '12px',
                                margin: '5px 0',
                                position: 'relative',
                                zIndex: 1,
                                '&:hover': {
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 1.1,
                                        left: 1.5,
                                        width: '19rem',
                                        height: '60px',
                                        backgroundColor: '#FCF0EC',
                                        zIndex: -1,
                                    },
                                },
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 1.1,
                                    left: 1.5,
                                    width: isScreenLg ? '18rem' : '19rem',
                                    height: '60px',
                                    backgroundColor: 'white',
                                    boxShadow:
                                        '1px 4px 8px rgba(0, 0, 0, 0.2),-1px 4px 8px rgba(0, 0, 0, 0.2)',
                                    zIndex: -1,
                                },
                            },
                            '& .Mui-selected': {
                                color: 'white!important',
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                zIndex: 2,
                                path: { fill: 'white' },
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 1.1,
                                    left: 1.5,
                                    width: isScreenLg ? '20rem' : '22rem',
                                    height: '60px',
                                    backgroundImage:
                                        'linear-gradient(90deg, #B75332 30%, #FAE1D8 100%)',
                                    zIndex: -1,
                                },
                            },
                            '& .Mui-selected::before': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Tab
                            icon={
                                <ExecutivesIcon
                                    fill={'none'}
                                    style={{ marginTop: '0.5rem', cursor: 'default' }}
                                />
                            }
                            iconPosition="start"
                            label={
                                <Typography
                                    sx={{
                                        marginTop: '0.5rem',
                                        marginLeft: '0.2rem',
                                        fontFamily: 'Poppins',
                                        lineHeight: '22px',
                                        fontSize: '20px',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                    }}
                                >
                                    {cmsContent.YageoFocus.title.content}
                                </Typography>
                            }
                            {...a11yProps(0)}
                        />
                        <Tab
                            icon={
                                <BoardMembersIcon fill={'none'} style={{ marginTop: '0.5rem' }} />
                            }
                            iconPosition="start"
                            label={
                                <Typography
                                    sx={{
                                        marginTop: '0.5rem',
                                        marginLeft: '0.2rem',
                                        fontFamily: 'Poppins',
                                        lineHeight: '22px',
                                        fontSize: '20px',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                    }}
                                >
                                    {cmsContent.KemetFocus.title.content}
                                </Typography>
                            }
                            {...a11yProps(1)}
                        />
                    </Tabs>

                    <TabPanel value={value} index={0}>
                        <FocusAccordion entries={cmsContent.YageoFocus.entries} />
                    </TabPanel>

                    <TabPanel value={value} index={1}>
                        <FocusAccordion entries={cmsContent.KemetFocus.entries} />
                    </TabPanel>
                </Box>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: '#F8F8F8',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        alignItems: 'center',
                    }}
                >
                    {mobileTabs.map((tab, index) => (
                        <Accordion
                            key={index}
                            expanded={mobileExpanded === index}
                            onChange={handleMobileAccordionChange(index)}
                            sx={{
                                marginBottom: '1rem',
                                backgroundColor: '#F2F2F2',
                                width: isScreenLg
                                    ? '565px'
                                    : isScreenMd
                                      ? '650px'
                                      : isScreenSm
                                        ? '422px'
                                        : isScreenXs
                                          ? '295px'
                                          : '833px',
                                borderLeft: '2px solid #E0E0E0',
                                borderRight: '2px solid #E0E0E0',
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: mobileExpanded === index ? '#B75332' : 'white',
                                    color: mobileExpanded === index ? 'white' : '#E56731',
                                    '& .MuiSvgIcon-root': {
                                        color: mobileExpanded === index ? 'white' : '#E56731',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CustomIcon
                                        component={tab.icon}
                                        expanded={mobileExpanded === index}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: isScreenXs ? '16px' : '20px',
                                            fontWeight: 500,
                                            marginLeft: '0.5rem',
                                        }}
                                    >
                                        {tab.label}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>{tab.content}</AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}
        </>
    );
};

export default SocialTab;
